import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import { useCashDrawer } from 'Src/feature/cash-drawer/useCashDrawer';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useCurrentEmployee } from 'Src/feature/employee/useCurrentEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { UsedModalSearchParamControl, useModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';

import { useOnErrorShowToast } from '../toasts/useOnErrorShowToast';

import { PayInOutTxPayload } from './payInOutService';
import { usePayInOut } from './usePayInOut';

const { Form, Field } = getTypedFormComponents<PayInOutTxPayload>();

const sectionId = 'usePayOut';

const usePayOut = (baseModalControl: UsedModalSearchParamControl) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();

  const confirmModal = useModalSearchParamControl({ value: 'pay-out-confirm' });
  const doneModal = useModalSearchParamControl({ value: 'pay-out-done' });

  const payInOuts = usePayInOut.query.getAll();
  const payOut = usePayInOut.mutate.payOut(useOnErrorShowToast({ title: t('payInOut.errors.payOut') }));
  const payOutReasons = usePayInOut.query.reason.options.payOut();
  const cashPaymentMethods = usePaymentMethod.query.cashMethods();
  const otherPaymentMethods = usePaymentMethod.query.otherMethods();
  const cashDrawer = useCashDrawer();
  const employee = useCurrentEmployee();

  const form = useForm<PayInOutTxPayload>({
    schema: object().shape({
      amount: number().label(t('payInOut.labels.payOutAmount')).min(0.01).required(),
      payInOutReasonId: number().label(t('labels.whatIsItFor')).required(),
    }),
  });

  const paymentMethodIdOptions = [
    ...(cashPaymentMethods.data ?? []).map((pm) => ({ value: pm.id, label: pm.name })),
    // grab Cash typed payment methods from otherMethods list
    ...(otherPaymentMethods.data ?? []).filter((pm) => pm.type === 'Cash').map((pm) => ({ value: pm.id, label: pm.name })),
  ];

  return {
    modal: {
      base: baseModalControl,
      confirm: confirmModal,
      done: doneModal,
    },
    form,
    canSubmit: form.state.canTrySubmit,
    options: {
      paymentMethodId: paymentMethodIdOptions,
      payOutReasons: payOutReasons.data,
    },
    employeeFullName: employee ? t('labels.util.employeeFullName', employee) : '',
    onOpen: () => baseModalControl.open(),
    onClose: () => {
      baseModalControl.close();
      confirmModal.close();
      doneModal.close();
      form.action.reset();
    },
    onSave: async () => {
      await form.action.submitIfValid(async ({ values }) => {
        const payInOutTransaction = {
          amount: values.amount,
          notes: values.notes,
          payInOutReasonId: values.payInOutReasonId,
          paymentMethodId: values.paymentMethodId ?? paymentMethodIdOptions[0].value,
        };
        // @ts-expect-error arg payInOutTransaction is incorrectly named "payOutTransaction" in the api docs and auto-generated service
        const response = await payOut.mutateAsync({ payInOutTransaction });
        log.info('onSave()', { response });
        if (response) {
          confirmModal.close();
          doneModal.open();
          cashDrawer.popDrawer();
          await payInOuts.refetch();
        }
      });
    },
    onRecordPayOut: async () => {
      await form.action.submitIfValid(async () => {
        confirmModal.open();
      });
    },
  };
};

export { Field, Form, usePayOut };
